import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Capacitor } from '@capacitor/core';
import { 
  User, 
  Camera, 
  Save, 
  Trash2, 
  Mail, 
  Phone, 
  MapPin,
  Eye,
  EyeOff,
  Upload,
  Lock,
  KeyRound,
  MessageSquare,
  Bell,
  BellOff,
  Check,
  AlertCircle
} from 'lucide-react';
import { usePushNotifications } from '../../hooks/usePushNotifications';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function ProfilePhotoUpload({ currentPhoto, onPhotoChange }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const photoUrl = currentPhoto 
    ? `${API_URL}/uploads/photos/${currentPhoto}`
    : null;

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await fetch(`${API_URL}/api/users/me/photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      onPhotoChange(data.profilePhoto);
      setPreview(null);
    } catch (err) {
      alert('Failed to upload photo: ' + err.message);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!confirm('Remove your profile photo?')) return;

    try {
      await api.delete('/users/me/photo');
      onPhotoChange(null);
    } catch (err) {
      alert('Failed to remove photo: ' + err.message);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-lg">
          {preview ? (
            <img src={preview} alt="Preview" className="w-full h-full object-cover" />
          ) : photoUrl ? (
            <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-hub-100">
              <User className="w-16 h-16 text-hub-400" />
            </div>
          )}
        </div>
        
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="absolute bottom-0 right-0 w-10 h-10 bg-hub-600 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-hub-700 transition-colors disabled:opacity-50"
          title="Upload photo"
        >
          {uploading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Camera className="w-5 h-5" />
          )}
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {currentPhoto && (
        <button
          onClick={handleRemovePhoto}
          className="mt-3 text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
        >
          <Trash2 className="w-4 h-4" />
          Remove photo
        </button>
      )}

      <p className="text-xs text-gray-500 mt-2">JPEG, PNG, or WebP. Max 5MB.</p>
    </div>
  );
}

function PushNotificationSettings() {
  const { 
    supported, 
    permission, 
    subscribed, 
    loading, 
    error,
    subscribe, 
    unsubscribe,
    sendTest
  } = usePushNotifications();

  const [testSent, setTestSent] = useState(false);
  const isNative = Capacitor.isNativePlatform();

  const handleToggle = async () => {
    if (subscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  const handleTest = async () => {
    const success = await sendTest();
    if (success) {
      setTestSent(true);
      setTimeout(() => setTestSent(false), 3000);
    }
  };

  // Native app (iOS/Android) - push controlled in device settings
  if (isNative) {
    return (
      <div className="pt-4 border-t border-gray-200">
        <div className="flex items-center gap-3 text-gray-600">
          <Bell className="w-5 h-5" />
          <div>
            <p className="font-medium">Push notifications</p>
            <p className="text-sm text-gray-500">
              This setting is controlled from your device's notification settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!supported) {
    return (
      <div className="pt-4 border-t border-gray-200">
        <div className="flex items-center gap-3 text-gray-500">
          <BellOff className="w-5 h-5" />
          <div>
            <p className="font-medium">Push notifications not supported</p>
            <p className="text-sm">Your browser doesn't support push notifications. Try using Chrome or Safari.</p>
          </div>
        </div>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="pt-4 border-t border-gray-200">
        <div className="flex items-start gap-3 text-yellow-800 bg-yellow-50 p-3 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Push notifications blocked</p>
            <p className="text-sm">
              You've blocked notifications for this site. To enable them, click the lock icon 
              in your browser's address bar and allow notifications.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-4 border-t border-gray-200 space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={subscribed}
              onChange={handleToggle}
              disabled={loading}
              className="mt-1 rounded"
            />
            <div>
              <div className="flex items-center gap-2">
                {subscribed ? (
                  <Bell className="w-4 h-4 text-green-600" />
                ) : (
                  <BellOff className="w-4 h-4 text-gray-400" />
                )}
                <span className="font-medium">Push notifications</span>
                {loading && (
                  <div className="w-4 h-4 border-2 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {subscribed 
                  ? 'You will receive notifications for new messages, gig updates, and payments even when the app is closed.' 
                  : 'Enable to receive instant notifications for messages, gig assignments, and payment updates.'}
              </p>
            </div>
          </label>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
          {error}
        </div>
      )}

      {subscribed && (
        <div className="flex items-center gap-3 pl-6">
          <button
            type="button"
            onClick={handleTest}
            disabled={loading || testSent}
            className="btn-secondary text-sm py-1.5"
          >
            {testSent ? (
              <>
                <Check className="w-4 h-4 mr-1 inline" />
                Sent!
              </>
            ) : (
              'Send Test Notification'
            )}
          </button>
          <span className="text-sm text-gray-500">
            Test that notifications are working
          </span>
        </div>
      )}
    </div>
  );
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  
  // Form state - smsOptIn defaults to FALSE for compliance (user must actively opt in)
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [shareContactInfo, setShareContactInfo] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await api.get('/users/me');
      setProfile(data);
      setName(data.name || '');
      setPhone(data.phone || '');
      setAddress(data.address || '');
      setShareContactInfo(data.shareContactInfo || false);
      // Only set true if explicitly true in database, otherwise false
      setSmsOptIn(data.smsOptIn === true);
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updated = await api.put('/users/me', {
        name,
        phone,
        address,
        shareContactInfo,
        smsOptIn
      });
      setProfile(updated);
      if (refreshUser) refreshUser();
      alert('Profile saved!');
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = (newPhoto) => {
    setProfile(prev => ({ ...prev, profilePhoto: newPhoto }));
    if (refreshUser) refreshUser();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Profile</h1>

      <div className="card">
        <div className="card-body">
          {/* Photo Upload */}
          <div className="flex justify-center mb-8 pb-8 border-b border-gray-200">
            <ProfilePhotoUpload 
              currentPhoto={profile?.profilePhoto}
              onPhotoChange={handlePhotoChange}
            />
          </div>

          {/* Profile Form */}
          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <label className="label">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <label className="label">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={profile?.email || ''}
                  className="input pl-10 bg-gray-50"
                  disabled
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Contact admin to change email</p>
            </div>

            <div>
              <label className="label">Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="input pl-10"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div>
              <label className="label">Mailing Address</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="input pl-10 min-h-[80px]"
                  placeholder="Street address, City, State ZIP"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Used for tax documents and payments</p>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shareContactInfo}
                  onChange={(e) => setShareContactInfo(e.target.checked)}
                  className="mt-1 rounded"
                />
                <div>
                  <div className="flex items-center gap-2">
                    {shareContactInfo ? (
                      <Eye className="w-4 h-4 text-green-600" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="font-medium">Share contact info with other contractors</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    When enabled, other contractors can see your phone and email in the directory
                  </p>
                </div>
              </label>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsOptIn}
                  onChange={(e) => setSmsOptIn(e.target.checked)}
                  className="mt-1 rounded"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <MessageSquare className={`w-4 h-4 ${smsOptIn ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="font-medium">Receive text message reminders</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    By checking this box, I consent to receive SMS text messages from Frequency Entertainment LLC 
                    regarding gig assignments, schedule reminders, schedule changes, and payment confirmations. 
                    Message frequency varies based on gig activity. Message and data rates may apply. 
                    Reply STOP to opt-out at any time. Reply HELP for assistance. 
                    View our <a href="/terms" target="_blank" className="text-hub-600 hover:underline">Terms of Service</a> and <a href="/privacy" target="_blank" className="text-hub-600 hover:underline">Privacy Policy</a>.
                  </p>
                </div>
              </label>
            </div>

            {/* Push Notifications Section */}
            <PushNotificationSettings />

            <div className="flex gap-3 pt-4">
              <button type="submit" className="btn-primary" disabled={saving}>
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Change Password Section - only for non-contractors or contractors with passwords */}
      <ChangePasswordSection />
    </div>
  );
}

function ChangePasswordSection() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Contractors typically use magic links, but can have passwords
  const isContractor = user?.role === 'CONTRACTOR';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSaving(true);

    try {
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card mt-6">
      <div className="card-body">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-hub-600" />
          <h3 className="font-semibold">Change Password</h3>
        </div>

        {isContractor && (
          <p className="text-sm text-gray-500 mb-4">
            As a contractor, you can also use magic link login. Setting a password is optional but allows you to log in faster.
          </p>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600 mb-4">
            Password changed successfully!
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Current Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="input pl-10"
                required
                autoComplete="current-password"
              />
            </div>
          </div>

          <div>
            <label className="label">New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input pl-10"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">At least 8 characters</p>
          </div>

          <div>
            <label className="label">Confirm New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input pl-10"
                required
                autoComplete="new-password"
              />
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={saving}>
            <KeyRound className="w-4 h-4" />
            {saving ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
